// src/views/whatsappInbox/whatsappinbox.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/header";
import { Grid, Spin } from "antd";
import { toast } from "react-hot-toast";
import logger, { Logger } from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchConversations,
  selectAllConversations,
  selectSelectedConversation,
  selectConversationsStatus,
  selectTotalConversations,
  setSelectedConversation,
  updateLastMessage,
  deleteConversation,
  clearSelectedConversation,
  assignConversationToUser
} from "../../store/slices/conversationsSlice";

import {
  fetchMessages,
  selectLead,
  addMessage,
  selectReplyContext,
  selectHasMoreMessages,
  selectLoadingOlderMessages,
  fetchOlderMessages
} from "../../store/slices/messagesSlice";

import {
  setSearchInChat,
  selectSearchInChat,
  setShowChatDetails,
  selectShowChatDetails,
  setShowAssignLeadDrawer,
  setShowAssignChatDrawer,
  setShowDeleteConfirmation,
  setLoadingState,
  // New Redux UI actions
  setOpenForm,
  selectOpenForm,
  setShowFbButton,
  selectShowFbButton,
  setAssignLeadHeaderTitle,
  selectAssignLeadHeaderTitle,
  setOpenLeadDrawer,
  selectOpenLeadDrawer,
  setShowLabelPopup,
  selectShowLabelPopup,
  setShowStatusPopup,
  selectShowStatusPopup
} from "../../store/slices/uiSlice";

import {
  selectWhatsappPhoneId,
  selectTeamMembers,
  selectIsWhatsappIntegrated,
  setCurrentUser
} from "../../store/slices/userSlice";

import {
  joinRoomRequest,
  leaveRoomRequest
} from "../../store/slices/socketSlice";

// Component imports
import InboxChatHeadRedux from "./InboxChatHeadRedux";
import IncomingMsgRedux from './IncomingMsgRedux';
import TypingActionAreaRedux from './TypingActionAreaRedux';
import ChatLeftMenuRedux from './ChatLeftMenuRedux';


import { assignChatToUser, assignLeadToUser, loadStatusData, scrollToBottom as scrollChatToBottom } from "./utils/whatsappInboxUtil";


import AddChat from "./AddChat";
import "./whatsapp.css";
import { EmptyChatScreen, LoaderContainer } from "./style";



// Service imports - will gradually replace with Redux
import { assignConversation } from "../../services/whatsapp";
import { assignLeadInList, getLeadDetail } from "../../services/leadService";

// Other component imports
import TaskDrawer from "../common/TaskDrawer";
import CreateTaskForm from "../../components/createTaskForm";
import CreateNoteForm from "../../components/createNoteForm";
import GlobalDrawer from "../../components/GlobalDrawer";
import UltimatePlanInfo from "../../components/UltimatePlanInfo/UltimatePlanInfo";
import FacebookIntegration from "../../components/FacebookIntegration";
import SelectLeadLabel from "../../components/label";
import SelectLeadStatus from "../../components/status";
import LeadMenu from "../followUps/LeadMenu/leadMenu";
import { LeadDataI } from "../leads/leads";
import MediaPreview from "./components/MediaPreview";
import LeadDetails from "../leads/leadDetails/leadDetails";
import DrawerComponent from "../../components/drawer";
import { leadFileList, setActivityList, setLeadTaskList, setNote } from "../../actions/actions";

const WhatsappInbox: React.FC = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  
  // Redux state selectors
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const isLoading = useAppSelector(selectConversationsStatus) === 'loading';
  const totalConversations = useAppSelector(selectTotalConversations);
  const lead = useAppSelector(selectLead);
  const searchInChat = useAppSelector(selectSearchInChat);
  const showUserDetails = useAppSelector(selectShowChatDetails);
  const phoneId = useAppSelector(selectWhatsappPhoneId);
  const teamMembers = useAppSelector(selectTeamMembers);
  const isWhatsappIntegrated = useAppSelector(selectIsWhatsappIntegrated);
  const replyContext = useAppSelector(selectReplyContext);
  
  // Redux UI state
  const openForm = useAppSelector(selectOpenForm);
  const showFbButton = useAppSelector(selectShowFbButton);
  const assignLeadHeaderTitle = useAppSelector(selectAssignLeadHeaderTitle);
  const openLeadDrawer = useAppSelector(selectOpenLeadDrawer);
  const showLabelPopup = useAppSelector(selectShowLabelPopup);
  const showStatusPopup = useAppSelector(selectShowStatusPopup);
  
  // Local refs
  const boxRef = useRef<HTMLDivElement>(null);
  const listInnerRef = useRef<any>(null);

const isLoadingOlderRef = useRef(false);

  // Local state (only for non-Redux state)
  const [labelData, setLabelData] = useState<any[]>([]);
  const [userdata, setUserdata] = useState<any>({});
  const [from, setFrom] = useState("");
  const [assignLeadDrawer, setAssignLeadDrawer] = useState(false);
  const [searchMsgText, setSearchMsgText] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [lead2, setLead] = useState<LeadDataI | undefined>();
  const [statusData, setStatusData] = useState<any>({});

  // Local state for status and labels 
  const [localOpenLeadDrawer, setLocalOpenLeadDrawer] = useState(openLeadDrawer);
const [localShowStatusPopup, setLocalShowStatusPopup] = useState(showStatusPopup);
const [localShowLabelPopup, setLocalShowLabelPopup] = useState(showLabelPopup);


// Add these 2 lines with your other selectors
const hasMoreMessages = useAppSelector(selectHasMoreMessages);
const loadingOlderMessages = useAppSelector(selectLoadingOlderMessages);

  // Grid breakpoints
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  // Component initialization
  // In your component's useEffect
useEffect(() => {
  logger.log("WhatsAppInbox - Component initialized with Redux");
  
  // Load status data
  const statusData = loadStatusData();
  setStatusData(statusData);
  
  // Load user data from localStorage
  let local: any = window.localStorage?.getItem("userData");
  if (local) {
    const userData = JSON.parse(local);
    setUserdata(userData);
    // Dispatch to Redux
    dispatch(setCurrentUser(userData));
    logger.log("WhatsAppInbox - User data loaded and dispatched to Redux", { 
      userId: userData._id,
      plan: userData.subscriptionPlanType 
    });
  }
  
  // Check WhatsApp integration
  checkWhatsAppIntegration();
  
  // Fetch conversations via Redux
  dispatch(fetchConversations({ page: 1, refresh: true }));
  logger.log("WhatsAppInbox - Initiated conversation fetch from Redux");
  
  // Component cleanup
  return () => {
    logger.log("WhatsAppInbox - Component unmounting, cleaning up Redux resources");
    if (selectedConversation) {
      dispatch(leaveRoomRequest());
      logger.log("WhatsAppInbox - Dispatched leaveRoomRequest for cleanup");
    }
  };
}, [dispatch]);
  



  // Sync Redux state to local state
useEffect(() => {
  setLocalShowLabelPopup(showLabelPopup);
}, [showLabelPopup]);

useEffect(() => {
  setLocalOpenLeadDrawer(openLeadDrawer);
}, [openLeadDrawer]);

useEffect(() => {
  setLocalShowStatusPopup(showStatusPopup);
}, [showStatusPopup]);

// Add this ONE useEffect with your other useEffects
useEffect(() => {
  const element = boxRef.current;
  if (!element) return;

  const handleScroll = () => {
    console.log('🔥 SCROLL: Triggered with scrollTop =', element.scrollTop);
    console.log('🔥 SCROLL: hasMoreMessages =', hasMoreMessages);
    console.log('🔥 SCROLL: loadingOlderMessages =', loadingOlderMessages);
    
    if (element.scrollTop <= 100 && hasMoreMessages && !loadingOlderMessages && selectedConversation?.convoId) {
      console.log('🔄 Loading older messages...');
      isLoadingOlderRef.current = true; // ✅ NEW: Flag that we're loading older messages
      dispatch(fetchOlderMessages(selectedConversation.convoId));
    } else {
      console.log('🔥 SCROLL: Conditions not met:', {
        scrollTopCheck: element.scrollTop <= 100,
        hasMoreMessages,
        notLoading: !loadingOlderMessages,
        hasConvoId: !!selectedConversation?.convoId
      });
    }
  };

  element.addEventListener('scroll', handleScroll, { passive: true });
  return () => element.removeEventListener('scroll', handleScroll);
}, [dispatch, selectedConversation?.convoId, hasMoreMessages, loadingOlderMessages]);
  
  // Check WhatsApp integration
  const checkWhatsAppIntegration = () => {
    try {
      const userIntigrationsList = JSON.parse(
        localStorage.getItem("userData") || ""
      ).userIntegrations;

      const findData = userIntigrationsList.find(
        (item: any) =>
          item?.integration?._id === "646b9286ae8ef73b33caec2e" && item?.isActive
      );

      const hasIntegration = !!findData;
      
      logger.log("WhatsAppInbox - Integration status:", hasIntegration);
      
      // Dispatch to Redux instead of local state
      dispatch(setShowFbButton(!hasIntegration));
    } catch (error) {
      logger.log("WhatsAppInbox - Error checking integration status", error);
      dispatch(setShowFbButton(true));
    }
  };
  






const handleLeadDetailsClick = useCallback(async () => {
  console.group('🔧 Opening Lead Details from Chat Head - DIRECT DISPATCH');
  
  // Check if lead data is available from the current conversation
  const leadData = lead;
  
  if (!leadData) {
    logger.log('❌ No lead available for selected conversation');
    toast.error("No lead details available for this conversation");
    console.groupEnd();
    return;
  }
  
  logger.log('📋 Opening lead details drawer with lead:', {
    leadId: leadData._id,
    name: leadData.name
  });
  
  try {
    // 🚀 DIRECT APPROACH: Call API and dispatch Redux actions immediately
    logger.log('📡 Fetching complete lead details...');
    const response = await getLeadDetail(leadData._id);
    
    if (response.data && response.data.status) {
      const responseData = response.data.data;
      
      logger.log('✅ Lead details loaded successfully:', {
        activities: responseData.activities?.length || 0,
        tasks: responseData.tasks?.length || 0,
        notes: responseData.notes?.length || 0
      });
      
      // 💥 DISPATCH ALL THE REDUX ACTIONS DIRECTLY
      console.log('🔄 Dispatching Redux state updates...');
      
      dispatch(setActivityList(responseData?.activities || []));
      logger.log('✅ Activities dispatched:', responseData.activities?.length || 0);
      
      dispatch(setLeadTaskList(responseData?.tasks || []));
      logger.log('✅ Tasks dispatched:', responseData.tasks?.length || 0);
      
      dispatch(setNote(responseData?.notes || []));
      logger.log('✅ Notes dispatched:', responseData.notes?.length || 0);
      
      dispatch(leadFileList(responseData?.files || []));
      logger.log('✅ Files dispatched:', responseData.files?.length || 0);
      
      // Set the complete lead data
      setLead(responseData as any);  // Type assertion to avoid TS issues
      setFrom("chat");
      
      // Open the drawer
      dispatch(setOpenLeadDrawer(true));
      
      console.log('🎉 Lead details drawer opened with ALL TABS POPULATED');
      
    } else {
      logger.log('❌ API returned unsuccessful response');
      toast.error("Failed to load lead details");
    }
  } catch (err) {
    console.error("❌ Error fetching lead details:", err);
    toast.error("Error loading lead details");
  }
  
  console.groupEnd();
}, [lead, setLead, setFrom, dispatch]);

// In WhatsappInbox.tsx - add to handleConnect function
const handleConnect = useCallback((user: any) => {
  logger.log("🔌 HANDLECONNECT CALLED WITH:", user);  // ← ADD THIS{
  if (!user) {
    logger.log("WhatsAppInbox - Cannot connect: No user provided");
    return;
  }
  
  logger.log("WhatsAppInbox - Connecting to conversation via Redux", {
    userId: user.id,
    convoId: user.convoId
  });
  
  // Select conversation in Redux
  dispatch(setSelectedConversation(user));
  
  // Use Redux action for socket room joining
  dispatch(joinRoomRequest({
    username: user?.name,
    room: user?.convoId
  }));
  
  // Fetch messages for this conversation
  if (user.convoId) {
    dispatch(fetchMessages(user.convoId));
    logger.log("WhatsAppInbox - Dispatched fetchMessages for", user.convoId);
    
    // Add explicit logging for scroll intention
    logger.log("WhatsAppInbox - Will scroll to bottom after messages load");
  }
}, [dispatch]);
  
  // Scroll to bottom helper

const scrollToBottom = useCallback(() => {
  Logger.log("Attempting to scroll to bottom");
  if (boxRef.current) {
    Logger.log("Box ref exists, scrolling to:", {
      scrollHeight: boxRef.current.scrollHeight,
      clientHeight: boxRef.current.clientHeight
    });
    
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
        Logger.log("Scroll complete, new scrollTop:", boxRef.current.scrollTop);
      }
    }, 100);
  } else {
    Logger.log("Box ref is null, cannot scroll");
  }
}, [boxRef]);
  
 

/// ✅ REPLACE with this auto-scroll logic that respects older message loading
useEffect(() => {
  // This effect will run whenever selectedConversation changes
  if (selectedConversation) {
    if (!isLoadingOlderRef.current) {
      // ✅ This is for new conversation - scroll to bottom
      logger.log("WhatsAppInbox - Selected conversation changed, scrolling to bottom");
      
      const scrollDelays = [300, 800];
      
      scrollDelays.forEach(delay => {
        setTimeout(() => {
          logger.log(`WhatsAppInbox - Executing scroll after ${delay}ms`);
          scrollToBottom();
        }, delay);
      });
    } else {
      // ✅ This is for older messages - maintain scroll position
      logger.log("WhatsAppInbox - Loading older messages, maintaining scroll position");
      isLoadingOlderRef.current = false; // Reset flag
    }
  }
}, [selectedConversation]);

  // Helper functions
  const addTaskHandler = () => {
    logger.log("WhatsAppInbox - Opening task form via Redux");
    dispatch(setOpenForm("task"));
  };

  const addNoteHandler = () => {
    logger.log("WhatsAppInbox - Opening note form via Redux");
    dispatch(setOpenForm("note")); 
  };

  const handleSearchInChat = (search: boolean) => {
    logger.log("WhatsAppInbox - Setting search in chat:", search);
    dispatch(setSearchInChat(search));
  };
  
  const backHandler = () => {
    logger.log("WhatsAppInbox - Back button pressed via Redux");
    
    if (showUserDetails) {
      dispatch(setShowChatDetails(false));
    } else {
      dispatch(clearSelectedConversation());
    }
  };
  
  const handleSetOpenLeadDrawer: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const newValue = typeof value === 'function' ? value(localOpenLeadDrawer) : value;
    dispatch(setOpenLeadDrawer(newValue));
  };

  const handleSetShowStatusPopup: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const newValue = typeof value === 'function' ? value(localShowStatusPopup) : value;
    dispatch(setShowStatusPopup(newValue));
  };
  

  const handleSetShowLabelPopup: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const newValue = typeof value === 'function' ? value(localShowLabelPopup) : value;
    dispatch(setShowLabelPopup(newValue));
  };

  const onLabelChange = (e: any, name: string) => {
    setLabelData((prev) => {
      let updatedLabelData: any[] = [];
      for (let label of labelData) {
        updatedLabelData.push({
          ...label,
          isLabelChecked:
            name == label.value ? !label.isLabelChecked : label.isLabelChecked,
        });
      }
      return updatedLabelData;
    });
  };
  
  const onStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log("WhatsAppInbox - Status changed:", e.target.value);
    setSelectedStatus(e.target.value);
  };
  
  // Handle chat assignment using Redux
  const handleAssignChat = useCallback((userId: string) => {
    if (!selectedConversation) {
      logger.log("WhatsAppInbox - Cannot assign chat: No selected conversation");
      return;
    }
    
    logger.log("WhatsAppInbox - Assigning chat", {
      userId,
      conversationId: selectedConversation._id
    });
    
    dispatch(setLoadingState({ key: 'assignChat', value: true }));
    
    assignChatToUser(selectedConversation._id, userId)
      .then(() => {
        setAssignLeadDrawer(false);
      })
      .finally(() => {
        dispatch(setLoadingState({ key: 'assignChat', value: false }));
      });
  }, [dispatch, selectedConversation]);
  
  // Handle lead assignment - keeping direct API call as per your request
  const handleAssignLead = async (userId: string) => {
    if (!selectedConversation || !lead) {
      logger.log("WhatsAppInbox - Cannot assign lead: Missing data");
      return;
    }
    
    try {
      logger.log("WhatsAppInbox - Assigning lead to user:", userId);
      dispatch(setLoadingState({ key: 'assignLead', value: true }));
      
      await assignLeadToUser(lead._id, userId);
      setAssignLeadDrawer(false);
    } catch (error) {
      // Error is already handled in the utility function with toast
      logger.log("WhatsAppInbox - Error in lead assignment:", error);
    } finally {
      dispatch(setLoadingState({ key: 'assignLead', value: false }));
    }
  };
  

  
  // Chat component (memoized)
  const chat = useMemo(() => {
    return (
      <div style={{ flex: 2, height: "100vh", display: "flex", flexDirection: "column" }} className="col px-0">
        <div className="chat-window-wrapper">
          {/* Chat header - fixed at top with enhanced dropdown */}
          <div className="inboxChatHead">
            <InboxChatHeadRedux 
              setSearchInChat={handleSearchInChat}
              addTaskHandler={addTaskHandler}
              addNoteHandler={addNoteHandler}
              onLeadDetailsClick={handleLeadDetailsClick} // Add this prop

            />
          </div>
          
          {/* Chat body - main content with scrolling */}
          <div className="inboxChat">
            <div className="flex-fill position-relative">
              <div className="convoBody" ref={boxRef} id="scrollable-box">
              {loadingOlderMessages && (
    <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(255,255,255,0.9)', borderRadius: '8px', margin: '8px' }}>
      <Spin size="small" /> Loading older messages...
    </div>
  )}
  
  {!hasMoreMessages && selectedConversation && (
    <div style={{ textAlign: 'center', padding: '12px', color: '#999', fontSize: '12px' }}>
      📅 Beginning of conversation
    </div>
  )}
                <IncomingMsgRedux 
                  scrollToBottom={scrollToBottom}
                />
              </div>
            </div>
            
            {/* Typing area - fixed at bottom */}
            <div className="typing-area-wrapper">
              <TypingActionAreaRedux
                scrollToBottom={scrollToBottom}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [scrollToBottom, handleSearchInChat]);
  
  // Empty chat screen (memoized)
  const emptyChatScreen = useMemo(() => {
    return (
      <EmptyChatScreen>
        <img src="/assets/img/emptyconvo.png" alt="No chat selected" />
        <h5 className="text-dark fm-gilroy fs-5">
          Select a conversation to start chatting
        </h5>
      </EmptyChatScreen>
    );
  }, []);
  
  // Check subscription plan
  if (userdata?.subscriptionPlanType?.toLowerCase() !== "ultimate") {
    logger.log("WhatsAppInbox - Not on Ultimate plan, showing plan info");
    return (
      <div id="main" className="main">
        <Header />
        <UltimatePlanInfo />
      </div>
    );
  }

  logger.log("WhatsAppInbox - Rendering with Redux state", {
    conversationsCount: conversations?.length,
    selectedConversation: selectedConversation?.id,
    showFbButton,
    showUserDetails,
    openForm
  });

  return (
    <div id="main" className="main mt-0 px-0 inboxPage">
      <Header />
       
      {showFbButton ? (
        <FacebookIntegration
          onSuccess={() => {
            logger.log("WhatsAppInbox - Facebook integration successful");
            dispatch(setShowFbButton(false));
            dispatch(fetchConversations({ page: 1, refresh: true }));
          }}
        />
      ) : (
        <>
          {isLoading && !conversations.length && (
            <LoaderContainer>
              <div className="h-100 d-flex align-items-center justify-content-center">
                <h5 className="text-dark fm-gilroy fs-6 text-center">
                  <Spin size="large" tip="Loading..." spinning={true}></Spin>
                </h5>
              </div>
            </LoaderContainer>
          )}
  
          <MediaPreview />
          <section className="flex-fill">
            <div className="row h-100 m-0">
              <ChatLeftMenuRedux 
  onConnect={handleConnect}  // ← ADD THIS
  listInnerRef={listInnerRef} 
/>
              
              {selectedConversation ? chat : <div style={{ flex: 2 }}>{emptyChatScreen}</div>}
            </div>
          </section>
        </>
      )}
  
      {/* Task Drawer */}
      <TaskDrawer
        openWithHeader={!!lead && !!openForm}
        setOpenWithHeader={() => {
          logger.log("WhatsAppInbox - Closing task/note drawer via Redux");
          dispatch(setOpenForm(null));
        }}
        onTaskDelete={() => {}}
        showDelete={false}
        drawerTitle={`Add ${openForm || ""}`}
        size="xs"
      >
        {openForm == "task" ? (
          <CreateTaskForm
            leadId={lead?._id}
            updateTaskValue={{}}
            action="add"
            status={""}
            drawerClose={() => {
              dispatch(setOpenForm(null));
            }}
            fetchTaskStatusFollowUps={() => {}}
          />
        ) : (
          <CreateNoteForm
            leadIds={[{ id: lead?._id }]}
            handleNotesSubmit={() => {
              dispatch(setOpenForm(null));
            }}
          />
        )}
      </TaskDrawer>
  
      {/* Assign Lead/Chat Drawer */}
      <GlobalDrawer
        openWithHeader={assignLeadDrawer}
        setOpenWithHeader={() => {
          logger.log("WhatsAppInbox - Closing assign lead/chat drawer");
          setAssignLeadDrawer(false);
        }}
        drawerTitle={assignLeadHeaderTitle || "Assign Lead"}
        size="xs"
      >
        <label className="form-label">Select team member</label>
        <div>
          {teamMembers?.map((item: any, ix: number) => (
            <button
              key={ix}
              onClick={() => {
                setAssignLeadDrawer(false);
                
                if (assignLeadHeaderTitle === "Assign Chat") {
                  logger.log("WhatsAppInbox - Assigning chat to", item.label);
                  handleAssignChat(item.value);
                } else {
                  logger.log("WhatsAppInbox - Assigning lead to", item.label);
                  handleAssignLead(item.value);
                }
              }}
              className="d-block bg-white w-100 p-3 rounded mb-2"
              style={{ margin: "8px 0px", textAlign: "left" }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </GlobalDrawer>
      
      {/* Lead management components */}
      <SelectLeadLabel
        showLabelPopup={showLabelPopup}
        toggleLeadLabel={() => dispatch(setShowLabelPopup(!showLabelPopup))}
        onLabelChange={onLabelChange}
        selectedLabel={lead?.label || []}
        setShowLabelPopup={handleSetShowLabelPopup}
        from={from}
        id={lead?._id || ""}
        leadList={""}
        getLeads={() => {}}
        setOpenWithHeader={() => {}}
        LabelData={labelData}
        updateEditedRow={(leads: any) => {
          logger.log("WhatsAppInbox - Labels updated", leads);
          if (Array.isArray(leads)) {
            setLead(leads[0]);
          } else {
            setLead(leads);
          }
        }}
      />
  
      <SelectLeadStatus
        showStatusPopup={showStatusPopup}
        toggleLeadStatus={() => dispatch(setShowStatusPopup(!showStatusPopup))}
        onStatusChange={onStatusChange}
        selectedStatus={selectedStatus}
        setShowStatusPopup={handleSetShowStatusPopup}
        from={from}
        id={lead?._id || ""}
        leadList={""}
        getLeads={() => {}}
        setOpenWithHeader={() => {}}
        updateEditedRow={(leads: any) => {
          logger.log("WhatsAppInbox - Status updated", leads);
          if (Array.isArray(leads)) {
            setLead(leads[0]);
          } else {
            setLead(leads);
          }
        }}
      />
  
      {lead && (
        <LeadMenu
          leadId={lead._id}
          leadLabelData={lead.label}
          setOpenWithHeader={() => {}}
          openLeadDrawer={openLeadDrawer}
          setOpenLeadDrawer={handleSetOpenLeadDrawer}
          onClickShowLabelPopup={() => dispatch(setShowLabelPopup(true))}
          onClickShowStatusPopup={() => dispatch(setShowStatusPopup(true))}
        />
      )}
  
      {/* Lead Details Drawer - Add this new section */}
     {lead && openLeadDrawer && (
  <DrawerComponent
    openWithHeader={openLeadDrawer}
    setOpenWithHeader={handleSetOpenLeadDrawer}
    customHeader={false}
    drawerTitle="Lead Details"
    size="lg"
    className="lead_details"
  >
    <LeadDetails
      key={`lead-details-${lead._id}-${openLeadDrawer}`}  // Add this key prop
      _id={lead._id}
      name={lead.name}
      integration={lead.integration}
      customSource={lead.customSource}
      createdAt={lead.createdAt}
      status={lead.status}
      saleValue={lead.saleValue}
      website={lead.website}
      label={lead.label}
      // Simple empty handlers
      deleteLeadFromDrawer={() => {}}
      copyLeadToList={() => {}}
      moveLeadToList={() => {}}
      onLabelClick={() => {}}
      onStatusClick={() => {}}
      setFrom={() => {}}
      handleEditModal={() => {}}
      handleLeadDetailsModalOpen={() => {}}
      onLeadFileUpload={() => {}}
      editLead={() => {}}
      leadFileDelete={() => {}}
    />
  </DrawerComponent>
)}
    </div>
  );
};

export default WhatsappInbox;